import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { Focusable, Tooltip, TooltipTrigger } from '@backstage/ui';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { useProjectAccess } from './projectAccess';
import { projectEnvironmentsTranslationRef } from './translation';

const ReadOnlyIcon = waadooIcons.readOnly;

/**
 * Where the badge sits, which decides its shape:
 *  - `label`: the eye and « Lecture seule » in a neutral `waadoo-pill` — the
 *    metadata row of the project and branch headers;
 *  - `icon`: the eye alone — a project's card on the home page, next to its
 *    name;
 *  - `sidebar`: the eye alone INSIDE a link (the project rows of the
 *    sidebar). No `Focusable` there, which would put a tab stop inside the
 *    link: the explanation is the native `title`, as the branch rows of the
 *    sidebar give theirs, and the row's own accessible name must say
 *    « Lecture seule » (`ProjectsNav.tsx`) — the icon is hidden from
 *    assistive technology.
 */
export type ReadOnlyBadgeVariant = 'label' | 'icon' | 'sidebar';

/**
 * « Lecture seule » for a project the signed-in user is only a member of
 * (`prj-<p>` without `prj-<p>-editors`, outside the admin tier), and nothing
 * otherwise: for editors and admins, while the access answer loads, and when
 * it failed (`useProjectAccess`). UX only — the gated routes decide.
 *
 * The tooltip says what the member cannot do; the accessible name stays the
 * short « Lecture seule ». Neutral ink and pill (`--bui-fg-secondary`,
 * `--bui-bg-neutral-2`): the tokens of the other badges, in both themes.
 */
export function ReadOnlyBadge(props: {
  project: string | undefined;
  variant?: ReadOnlyBadgeVariant;
}) {
  const { project, variant = 'icon' } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const levelOf = useProjectAccess();

  if (!project || levelOf(project) !== 'read') {
    return null;
  }

  const label = t('readOnly.label');
  const tooltip = t('readOnly.tooltip');
  const icon = <ReadOnlyIcon aria-hidden focusable="false" />;

  if (variant === 'sidebar') {
    return (
      <span
        className="waadoo-read-only"
        data-variant="sidebar"
        title={tooltip}
        aria-hidden="true"
        data-testid="read-only-badge"
      >
        {icon}
      </span>
    );
  }

  return (
    <TooltipTrigger>
      <Focusable>
        <span
          role="img"
          aria-label={label}
          className={
            variant === 'label'
              ? 'waadoo-pill waadoo-read-only'
              : 'waadoo-read-only'
          }
          data-variant={variant}
          data-testid="read-only-badge"
        >
          {icon}
          {variant === 'label' ? <span>{label}</span> : null}
        </span>
      </Focusable>
      <Tooltip>{tooltip}</Tooltip>
    </TooltipTrigger>
  );
}
